import { DialogComponent } from '@theme/dialog';
import { renderWishlistProducts, bindProductRemoveButtons } from '@theme/wishlist-products';

const CACHE_KEY = 'wishlist_lists_cache';

const prefersReducedMotion = () =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const onAnimationEnd = (element, callback) => {
  const handler = (event) => {
    if (event.target !== element) return;
    element.removeEventListener('animationend', handler);
    callback();
  };
  element.addEventListener('animationend', handler);
};

class WishlistDrawerComponent extends DialogComponent {
  #lists = [];
  #view = 'lists';
  #selectedList = null;
  #isLoading = false;

  connectedCallback() {
    super.connectedCallback();
    this.#handleOpenWishlist = this.#handleOpenWishlist.bind(this);
    this.#handleRefresh = this.#handleRefresh.bind(this);
    this.#handleOpenDetails = this.#handleOpenDetails.bind(this);

    window.addEventListener('open-wishlist', this.#handleOpenWishlist);
    window.addEventListener('refresh-wishlist-lists', this.#handleRefresh);
    window.addEventListener('open-wishlist-details', this.#handleOpenDetails);

    const clearBtn = this.querySelector('[ref="clearButton"]');
    if (clearBtn) clearBtn.addEventListener('click', () => this.#deleteList());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('open-wishlist', this.#handleOpenWishlist);
    window.removeEventListener('refresh-wishlist-lists', this.#handleRefresh);
    window.removeEventListener('open-wishlist-details', this.#handleOpenDetails);
  }

  open() {
    this.#view = 'lists';
    this.#selectedList = null;
    this.showDialog();
    this.#loadFromCache();
    this.#fetchLists();
  }

  close() {
    this.closeDialog();
  }

  #handleOpenWishlist = () => {
    this.open();
  };

  #handleRefresh = () => {
    this.#fetchLists();
  };

  #handleOpenDetails = (event) => {
    const list = event.detail?.list;
    if (list) {
      this.#openDetails(list);
    }
  };

  #loadFromCache() {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        this.#lists = JSON.parse(cached);
        this.#render();
      }
    } catch (_) {
      // no-op
    }
  }

  #saveToCache() {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(this.#lists));
    } catch (_) {
      // no-op
    }
  }

  #invalidateCache() {
    localStorage.removeItem(CACHE_KEY);
  }

  #fetchLists() {
    if (!window._swat || !window._swat.fetchLists) {
      this.#waitForSwym(() => this.#fetchLists());
      return;
    }

    if (this.#lists.length === 0) {
      this.#isLoading = true;
      this.#render();
    }

    window._swat.fetchLists({
      callbackFn: (lists) => {
        this.#formatLists(lists).then((formatted) => {
          const changed = this.#listsFingerprint(formatted) !== this.#listsFingerprint(this.#lists);

          this.#lists = formatted;
          this.#saveToCache();
          this.#isLoading = false;

          if (this.#view === 'details' && this.#selectedList) {
            const updated = this.#lists.find(l => l.id === this.#selectedList.id);
            this.#selectedList = updated || null;
            if (!this.#selectedList) {
              this.#view = 'lists';
              this.#render();
              return;
            }
          }

          if (changed) {
            this.#render();
          }
        }).catch(() => {
          this.#isLoading = false;
          this.#render();
        });
      },
      errorFn: () => {
        this.#isLoading = false;
        this.#render();
      }
    });
  }

  async #formatLists(lists) {
    const formatted = [];

    for (const list of lists) {
      const products = await this.#enrichProducts(list.listcontents || []);
      const formattedProducts = products.map(p => this.#formatProduct(p));

      formatted.push({
        id: list.lid,
        name: list.lname || this.#t('unnamed_list'),
        products: formattedProducts,
        productCount: formattedProducts.length,
        displayImages: formattedProducts.slice(0, 3).map(p => p.image)
      });
    }

    return formatted;
  }

  async #enrichProducts(products) {
    const withUrl = products.filter(p => p.du);
    if (withUrl.length === 0) return products;

    const enriched = new Map();

    await Promise.allSettled(withUrl.map(async (product) => {
      try {
        const productPath = new URL(product.du).pathname;
        const response = await fetch(`${productPath}?sections=wishlist-product-meta`);
        if (!response.ok) return;

        const sections = await response.json();
        const meta = JSON.parse(sections['wishlist-product-meta']);
        const variant = meta.variants?.find(v => String(v.id) === String(product.epi));

        enriched.set(`${product.empi}-${product.epi}`, {
          productName: meta.product_name || null,
          variantName: meta.variant_name || null,
          options: variant?.options || [],
          hasOnlyDefaultVariant: meta.has_only_default_variant,
          op: variant?.compare_at_price ? variant.compare_at_price / 100 : null,
          image: variant?.image || meta.featured_image || null
        });
      } catch (_) {
        // silently skip failed enrichment
      }
    }));

    return products.map(p => {
      const key = `${p.empi}-${p.epi}`;
      const extra = enriched.get(key);
      if (extra) {
        p._productName = extra.productName;
        p._variantName = extra.variantName;
        p._options = extra.options;
        p._hasOnlyDefaultVariant = extra.hasOnlyDefaultVariant;
        if (extra.op && !p.op) p.op = extra.op;
        if (extra.image && !p.iu) p.iu = extra.image;
      }
      return p;
    });
  }

  #formatProduct(p) {
    const price = typeof p.pr === 'number' ? p.pr : parseFloat(p.pr) || 0;
    const compareAt = p.op ? (typeof p.op === 'number' ? p.op : parseFloat(p.op)) : null;

    let percentOff = null;
    if (compareAt && compareAt > price && compareAt > 0) {
      percentOff = Math.floor(((compareAt - price) * 100) / compareAt);
    }

    const options = !p._hasOnlyDefaultVariant && p._options?.length
      ? p._options.join('/')
      : null;

    return {
      empi: p.empi,
      epi: p.epi,
      productName: p._productName || '',
      variantName: p._variantName || '',
      options,
      title: p.dt || '',
      url: p.du || '',
      image: p.iu || '',
      price: this.#formatPrice(price),
      compareAtPrice: compareAt ? this.#formatPrice(compareAt) : null,
      percentOff,
      inStock: p.stk == null || p.stk > 0
    };
  }

  #formatPrice(amount) {
    const moneyFormat = window.SwymOverrideMoneyFormat || '{{amount}}';
    const formatted = (Math.round(amount * 100) / 100).toFixed(2).replace(/\.00$/, '');
    return moneyFormat.replace(/\{\{\s*amount\s*\}\}/, formatted)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(amount).toString())
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, formatted.replace('.', ','));
  }

  #openDetails(list) {
    this.#view = 'details';
    this.#selectedList = list;
    this.#render();
  }

  #removeProduct(product) {
    if (!this.#selectedList || !window._swat) return;

    const content = this.querySelector('[ref="content"]');
    const row = content?.querySelector(
      `.wishlist-product[data-empi="${product.empi}"][data-epi="${product.epi}"]`
    );

    const removeFromModel = () => {
      this.#selectedList.products = this.#selectedList.products.filter(
        p => !(String(p.empi) === String(product.empi) && String(p.epi) === String(product.epi))
      );
      this.#selectedList.productCount = this.#selectedList.products.length;
      this.#selectedList.displayImages = this.#selectedList.products.slice(0, 3).map(p => p.image);
    };

    const deleteFromAPI = () => {
      window._swat.deleteFromList(
        String(this.#selectedList.id),
        { epi: product.epi, empi: product.empi, du: product.url },
        () => {
          this.#invalidateCache();
          if (window.invalidateWishlistCache) window.invalidateWishlistCache();
          if (window.updateWishlistButtonStates) window.updateWishlistButtonStates();
          this.#fetchLists();
        },
        () => { }
      );
    };

    if (!row || prefersReducedMotion()) {
      removeFromModel();
      this.#render();
      deleteFromAPI();
      return;
    }

    row.style.setProperty('--row-height', `${row.clientHeight}px`);
    row.classList.add('removing');
    onAnimationEnd(row, () => {
      row.remove();
      removeFromModel();

      if (this.#selectedList.products.length === 0) {
        this.#render();
      } else {
        this.#updateFooter();
      }

      deleteFromAPI();
    });
  }

  #deleteList() {
    if (!this.#selectedList || !window._swat) return;

    window._swat.deleteList(
      this.#selectedList.id,
      () => {
        this.#invalidateCache();
        if (window.invalidateWishlistCache) window.invalidateWishlistCache();
        if (window.updateWishlistButtonStates) window.updateWishlistButtonStates();
        this.#view = 'lists';
        this.#selectedList = null;
        this.#fetchLists();
      },
      () => { }
    );
  }

  #waitForSwym(callback) {
    let retries = 0;
    const check = () => {
      if (window._swat && window._swat.fetchLists) {
        callback();
      } else if (retries < 100) {
        retries++;
        setTimeout(check, 100);
      } else {
        this.#isLoading = false;
        this.#render();
      }
    };
    check();
  }

  #t(key) {
    const translations = window.__wishlistTranslations || {};
    return translations[key] || key;
  }

  #listsFingerprint(lists) {
    return lists.map(l =>
      `${l.id}:${l.products.map(p => `${p.empi}-${p.epi}`).join(',')}`
    ).join('|');
  }

  #render() {
    const content = this.querySelector('[ref="content"]');
    if (!content) return;

    if (this.#view === 'details' && this.#selectedList) {
      this.#renderDetails(content);
    } else {
      this.#renderLists(content);
    }

    this.#updateFooter();
  }

  #updateFooter() {
    const footer = this.querySelector('[ref="footer"]');
    if (!footer) return;

    const showFooter = this.#view === 'details'
      && this.#selectedList
      && this.#selectedList.products.length > 0;

    footer.hidden = !showFooter;
  }

  #renderLists(container) {
    if (this.#isLoading && this.#lists.length === 0) {
      container.innerHTML = `
        <div class="wishlist-drawer__loading">
          <p>${this.#t('loading_wishlists')}</p>
        </div>
      `;
      return;
    }

    const allProducts = this.#lists.flatMap(l => l.products);

    if (allProducts.length === 0) {
      container.innerHTML = `
        <div class="wishlist-drawer__empty">
          <p class="wishlist-drawer__empty-title">${this.#t('empty_title')}</p>
          <p class="wishlist-drawer__empty-description">${this.#t('empty_description')}</p>
          <a href="/" class="wishlist-drawer__continue-shopping button">${this.#t('continue_shopping')}</a>
        </div>
      `;
      return;
    }

    if (this.#lists.length === 1) {
      this.#openDetails(this.#lists[0]);
      return;
    }

    const listsHtml = this.#lists.map(list => `
      <button class="wishlist-drawer__list-card" data-list-id="${list.id}">
        <div class="wishlist-drawer__list-images">
          ${list.displayImages.map(img => `
            <img src="${img}" alt="" width="80" height="100" loading="lazy">
          `).join('')}
        </div>
        <div class="wishlist-drawer__list-info">
          <span class="wishlist-drawer__list-name">${list.name}</span>
          <span class="wishlist-drawer__list-count">${list.productCount} ${list.productCount === 1 ? this.#t('item') : this.#t('items')}</span>
        </div>
      </button>
    `).join('');

    container.innerHTML = `<div class="wishlist-drawer__lists">${listsHtml}</div>`;

    for (const card of container.querySelectorAll('[data-list-id]')) {
      card.addEventListener('click', () => {
        const list = this.#lists.find(l => String(l.id) === card.dataset.listId);
        if (list) this.#openDetails(list);
      });
    }
  }

  #renderDetails(container) {
    const list = this.#selectedList;
    if (!list) return;

    container.innerHTML = renderWishlistProducts(list.products, (key) => this.#t(key));
    bindProductRemoveButtons(container, list.products, (product) => this.#removeProduct(product));
  }
}

if (!customElements.get('wishlist-drawer-component')) {
  customElements.define('wishlist-drawer-component', WishlistDrawerComponent);
}
